/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { MeetingApp } from '@exploring-infinities/meet';
import { Navigate, RouteObject, RouterProvider } from 'react-router';

import { AppFallback, AuthFallback } from '@/components/errors';
import { paths } from '@/config/paths';
// Use DataDog's createBrowserRouter for automatic route tracking
// This wraps react-router's createBrowserRouter with RUM view tracking
import { createBrowserRouter } from '@/instrument';
import { useGoogleLoginTracker } from '@/lib/analytics';

import { ProtectedRoute, PublicRoute } from './route-guards';
import { MaverickLayout } from './routes/app/maverick';
import AppRoot from './routes/app/root';
import AuthRoot from './routes/auth/root';

const convert = (m: any) => {
  const { default: Component, ...rest } = m;
  return {
    ...rest,
    Component,
  };
};

const baseRoutes: RouteObject[] = [
  {
    path: paths.auth.root.path,
    element: (
      <PublicRoute>
        <AuthRoot />
      </PublicRoute>
    ),
    ErrorBoundary: AuthFallback,
    children: [
      {
        index: true,
        lazy: () => import('./routes/auth/login').then(convert),
      },
      {
        path: paths.auth.register.path,
        lazy: () => import('./routes/auth/register').then(convert),
      },
      {
        path: paths.auth.login.path,
        lazy: () => import('./routes/auth/login').then(convert),
      },
      {
        path: paths.auth.forgotPassword.path,
        lazy: () => import('./routes/auth/forgot-password').then(convert),
      },
    ],
  },
  {
    path: paths.app.root.path,
    element: (
      <ProtectedRoute>
        <AppRoot />
      </ProtectedRoute>
    ),
    ErrorBoundary: AppFallback,
    children: [
      {
        index: true,
        lazy: () => import('./routes/app/dashboard').then(convert),
      },
      {
        path: paths.app.modules.path,
        lazy: () => import('./routes/app/modules').then(convert),
      },
      {
        path: paths.app.brainGym.path,
        lazy: () => import('./routes/app/brain-gym').then(convert),
      },
      {
        path: paths.app.moduleReports.path,
        lazy: () => import('./routes/app/module-reports').then(convert),
      },
      {
        path: paths.app.profiles.path,
        lazy: () => import('./routes/app/profiles').then(convert),
      },
      {
        path: paths.profile.select.path,
        lazy: () => import('./routes/app/profile/select').then(convert),
      },
      {
        path: paths.app.onboarding.path,
        lazy: () => import('./routes/app/onboarding').then(convert),
      },
    ],
  },
];

const maverickRoute = paths.maverick
  ? [
      {
        path: paths.maverick.class.path,
        element: (
          <ProtectedRoute>
            <MaverickLayout />
          </ProtectedRoute>
        ),
        children: [
          {
            path: paths.maverick.class.meeting.path,
            element: <MeetingApp key="main" />,
          },
          {
            path: paths.maverick.class.parentMeeting.path,
            element: <MeetingApp key="breakout" />,
          },
        ],
      },
    ]
  : [];

const oldAppRedirectRoutes = [
  {
    path: paths.oldApp.assessment.path,
    lazy: () => import('./routes/old-app-redirect').then(convert),
  },
  {
    path: paths.oldApp.quiz.path,
    lazy: () => import('./routes/old-app-redirect').then(convert),
  },
  {
    path: paths.oldApp.reportRaw.path,
    lazy: () => import('./routes/old-app-redirect').then(convert),
  },
  {
    path: paths.oldApp.quizJoin.path,
    lazy: () => import('./routes/old-app-redirect').then(convert),
  },
];

const legacyRouteRedirects: RouteObject[] = [
  {
    path: paths.oldApp.selectProfile.path,
    element: <Navigate to={paths.profile.select.path} />,
  },
];

const standaloneRoutes: RouteObject[] = [
  {
    path: paths.app.videoPlayer.path,
    lazy: async () => {
      const { default: Component } = await import('./routes/app/video-player');
      return {
        element: (
          <ProtectedRoute>
            <Component />
          </ProtectedRoute>
        ),
      };
    },
  },
  {
    path: paths.app.batchPausePolicy.path,
    lazy: async () => {
      const { default: Component } = await import(
        './routes/app/batch-pause-policy'
      );
      return {
        element: <Component />,
      };
    },
  },
];

const notFoundRoute = {
  path: '*',
  lazy: () => import('./routes/not-found').then(convert),
};

export const router = createBrowserRouter([
  ...baseRoutes,
  ...maverickRoute,
  ...standaloneRoutes,
  ...oldAppRedirectRoutes,
  ...legacyRouteRedirects,
  notFoundRoute,
]);

export const AppRouter = () => {
  // Track Google login success/failure after OAuth redirect
  useGoogleLoginTracker();

  return <RouterProvider router={router} />;
};
